import { Outlet, useLocation } from 'react-router-dom';
import { AppShell } from '@mantine/core';
import { useFeatureFlag } from '../../stores/featureFlags';
import { useElementSize } from '@mantine/hooks';
import { ImpersonationBanner } from '../ImpersonationBanner';
import { InSessionHeaderBar } from './InSessionHeaderBar';
import { CoachLayout } from './CoachLayout';
import { ParticipantLayout } from './ParticipantLayout';
import classes from './InSessionLayout.module.css';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

/**
 * MED-1010 — Immersive ("submersive") shell for the shared in-session screen
 * used by both coaches (`/coach/.../active`) and peer leaders
 * (`/participant/.../active`).
 *
 * The top bar deliberately offers NO way to navigate away from an active
 * session: global nav links and the user-menu actions are removed entirely
 * (AC #1), the Medley logo is shown but non-clickable (AC #2), and only the
 * signed-in user's name + role remain (AC #3). End Session — rendered by the
 * page itself — is the only intended exit (AC #4).
 *
 * Note: the Figma mock (node 2044:16765) still shows Dashboard/Resources nav
 * pills, but AC #1 explicitly overrides that ("goes beyond current Figma
 * annotation"). Figma is followed for visual treatment only — bar color
 * (#203358) and the lighter-gray page background (#F2F0ED, AC #5).
 *
 * Gated by `live_session_delivery`: when the flag is OFF we fall back to the
 * normal app chrome so users keep their navigation (the page renders its own
 * "not available" notice and would otherwise be unreachable to leave).
 *
 * `forceImmersive` bypasses the flag fallback — used by the dev-only static
 * preview route so the immersive shell can be inspected without a live session.
 * `contextOverride` lets that preview render the coach vs. peer-leader variant
 * independently of the URL.
 */
export interface InSessionLayoutProps {
  forceImmersive?: boolean;
  contextOverride?: 'coach' | 'participant';
}

export function InSessionLayout({
  forceImmersive = false,
  contextOverride,
}: InSessionLayoutProps = {}) {
  const liveSessionDeliveryEnabled = useFeatureFlag('live_session_delivery');
  const location = useLocation();
  const isParticipantContext = contextOverride
    ? contextOverride === 'participant'
    : location.pathname.startsWith('/participant/');

  // Measure the banner, because its height changes with its text and the
  // screen width. A fixed height let the page cover the nav (MDF-251).
  const { ref: bannerRef, height: bannerHeight } = useElementSize();
  // MED-1383: the immersive shell skips the role layouts, so it names the tab itself.
  useDocumentTitle(isParticipantContext ? 'Medley' : 'Medley Coach');

  // Feature off → preserve the existing, fully-navigable chrome. Both layouts
  // render an <Outlet />, so this delegates without duplicating their markup.
  if (!liveSessionDeliveryEnabled && !forceImmersive) {
    return isParticipantContext ? <ParticipantLayout /> : <CoachLayout />;
  }

  // Figma top bar is 68px. The impersonation banner adds its measured height.
  const headerHeight = 68 + bannerHeight;

  // Header background is role-based, matching the user's normal app chrome:
  // coach slate (CoachLayout) vs peer-leader green (ParticipantLayout). The
  // Figma mock's blue is intentionally not used here.
  const headerBg = isParticipantContext ? '#2D4739' : '#2D3748';

  return (
    <AppShell header={{ height: headerHeight }} padding={0}>
      <AppShell.Header
        className={classes.header}
        style={{ height: headerHeight, backgroundColor: headerBg }}
      >
        <div ref={bannerRef}>
          <ImpersonationBanner />
        </div>
        {/* MED-1068 — reduced header extracted to a shared component so the
            Session Preview surface can render the same bar beneath its frame. */}
        <InSessionHeaderBar contextOverride={contextOverride} />
      </AppShell.Header>

      <AppShell.Main
        className={classes.main}
        style={{ minHeight: `calc(100vh - ${headerHeight}px)` }}
      >
        <Outlet />
      </AppShell.Main>
    </AppShell>
  );
}

export default InSessionLayout;
